import axios from "axios";
import { toast } from "sonner";
import { clearAuthSession, isStaleWorkspaceError, resetStaleWorkspace } from "@/lib/authSession";

// Migrate legacy localStorage keys (ReformaPro → Factlify rebranding)
if (typeof window !== "undefined" && window.localStorage) {
  const legacyPairs = [["reforma_token", "factlify_token"], ["reforma_workspace", "factlify_workspace"]];
  for (const [old, fresh] of legacyPairs) {
    const v = localStorage.getItem(old);
    if (v && !localStorage.getItem(fresh)) localStorage.setItem(fresh, v);
    if (v) localStorage.removeItem(old);
  }
}

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("factlify_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  const ws = localStorage.getItem("factlify_workspace");
  if (ws) cfg.headers["X-Workspace-Id"] = ws;
  return cfg;
});

/**
 * Response interceptor: when a request hits a plan limit (HTTP 402 with a
 * structured detail from `plan_limits.py`), we show a friendly toast with an
 * "Upgrade" action linking to /billing. The original promise still rejects so
 * the calling component can render its own UI if it wants to.
 */
let lastPlanToastAt = 0;

// Timestamp-based deduplication: prevents concurrent 401s from firing multiple times.
// Unlike a module-level boolean, a timestamp survives logical resets without
// blocking legitimate re-authentication after a real logout/login cycle.
let _lastReauthAt = 0;

// Same debounce pattern for stale-workspace recovery (403 on every endpoint
// when localStorage keeps a workspace the user no longer belongs to).
let _lastWorkspaceResetAt = 0;

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const data = err?.response?.data?.detail;
    const code = data?.code;

    // Plan quota / feature lock
    if (status === 402 && (code === "plan_quota_exceeded" || code === "plan_feature_locked")) {
      const now = Date.now();
      if (now - lastPlanToastAt > 2000) {
        lastPlanToastAt = now;
        toast.error(data.message || "Límite del plan alcanzado", {
          duration: 7000,
          action: { label: "Mejorar plan", onClick: () => { window.location.href = "/billing"; } },
        });
      }
    }

    // Expired or invalid session.
    //
    // We dispatch a custom event instead of doing window.location.replace so
    // there is NO hard reload. AuthContext listens, calls checkAuth() which
    // returns null, and ProtectedRoute does a soft React Router redirect to
    // /login. This breaks the reload loop completely.
    //
    // Guards:
    //  1. HTTP 401 on a non-auth endpoint.
    //  2. Token in localStorage — confirms the user was authenticated.
    //     Unauthenticated requests (e.g. on /login) get 401 legitimately.
    //  3. Debounce: at most once per 3 s so concurrent 401s only fire once.
    if (
      status === 401 &&
      !err?.config?.url?.includes("/auth/") &&
      localStorage.getItem("factlify_token")
    ) {
      const now = Date.now();
      if (now - _lastReauthAt > 3000) {
        _lastReauthAt = now;
        clearAuthSession();
        sessionStorage.setItem("factlify_session_expired", "1");
        window.dispatchEvent(new Event("factlify:needs-reauth"));
      }
    }

    // Stale workspace in localStorage.
    //
    // The backend rejects EVERY endpoint (including /workspaces/mine, the
    // self-healing path in WorkspaceContext) with 403 "Not a member of this
    // workspace" when X-Workspace-Id is stale, so without this the user is
    // stuck until they clear storage by hand. Recovery: drop only the
    // workspace key (session token is still valid) and reload once — the next
    // requests go without the header and the backend falls back to the user's
    // default workspace. Cannot loop: after the reset no stale header is sent,
    // and WorkspaceContext only re-stores ids validated against /workspaces/mine.
    if (isStaleWorkspaceError(err)) {
      const now = Date.now();
      if (now - _lastWorkspaceResetAt > 3000) {
        _lastWorkspaceResetAt = now;
        if (resetStaleWorkspace()) {
          window.location.reload();
        }
      }
    }

    return Promise.reject(err);
  }
);
